import React from 'react'
import styled from 'styled-components'
import {
  Button,
  ColorPicker as AccessibleColorPicker,
  Dialog,
  DialogTrigger,
  Popover,
  ColorSwatch,
  ColorArea,
  ColorSlider,
  ColorField,
  ColorThumb,
  SliderOutput,
  SliderTrack,
  SliderThumb,
  Label,
  Input,
} from 'react-aria-components'
import { accessibilityColors } from '@edulastic/colors'

const ColorPicker = ({ color, onChangeColor, ariaLabel }) => {
  const onColorChangeHandler = (selectedColorObject) => {
    const selectedColor = {
      color: selectedColorObject.toString('hex'),
      alpha: selectedColorObject.alpha * 100,
      open: true,
    }
    onChangeColor(selectedColor)
  }

  return (
    <AccessibleColorPicker defaultValue={color}>
      <DialogTrigger>
        <StyledButton aria-label={ariaLabel || 'Color Picker'}>
          <StyledColorSwatch color={color} />
        </StyledButton>
        <Popover placement="bottom start">
          <StyledDialog>
            <StyledColorArea
              defaultValue={color}
              colorSpace="hsb"
              xChannel="saturation"
              yChannel="brightness"
              onChange={onColorChangeHandler}
            >
              <StyleColorThumb />
            </StyledColorArea>
            <StyledColorSlider
              onChange={onColorChangeHandler}
              colorSpace="hsb"
              channel="hue"
            >
              <Label />
              <StyledSliderOutput />
              <StyledSliderTrack>
                <StyleSliderThumb />
              </StyledSliderTrack>
            </StyledColorSlider>
            <StyledColorSlider
              colorSpace="hsb"
              onChange={onColorChangeHandler}
              channel="alpha"
            >
              <Label />
              <StyledSliderOutput />
              <StyledSliderTrack>
                <StyleSliderThumb />
              </StyledSliderTrack>
            </StyledColorSlider>
            <StyledColorField label="Hex">
              <Label>Hex</Label>
              <StyledInput
                color={color}
                aria-label="Hex color"
                defaultValue={color}
              />
            </StyledColorField>
          </StyledDialog>
        </Popover>
      </DialogTrigger>
    </AccessibleColorPicker>
  )
}

export default ColorPicker

const StyledButton = styled(Button)`
  background: none;
  border: none;
  padding: 0;
  display: flex;
  align-items: center;
  gap: 8px;
  outline: none;
  border-radius: 4px;
  appearance: none;
  vertical-align: middle;
  font-size: 1rem;
  color: var(--text-color);

  &[data-focus-visible] {
    outline: 2px solid var(--focus-ring-color);
    outline-offset: 2px;
  }
`

const StyleSliderThumb = styled(SliderThumb)`
  border: 2px solid white;
  box-shadow: 0 0 0 1px black, inset 0 0 0 1px black;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  box-sizing: border-box;

  &[data-focus-visible] {
    width: 24px;
    height: 24px;
  }
  top: 50%;
`

const StyledSliderTrack = styled(SliderTrack)`
  height: 28px;
  grid-area: track;
  border-radius: 4px;
`

const StyledSliderOutput = styled(SliderOutput)`
  grid-area: output;
`

const StyledColorSwatch = styled(ColorSwatch)`
  width: 24px;
  height: 24px;
  border-radius: 4px;
  box-shadow: inset 0 0 0 1px rgba(0, 0, 0, 0.1);
  margin-left: 4px;
`

const StyledColorArea = styled(ColorArea)`
  width: 192px;
  height: 192px;
  border-radius: 4px;
  flex-shrink: 0;
`

const StyledDialog = styled(Dialog)`
  outline: none;
  padding: 15px;
  display: flex;
  flex-direction: column;
  gap: 8px;
  min-width: 192px;
  max-height: inherit;
  box-sizing: border-box;
  overflow: auto;
  background: white;
  border: 1px solid ${accessibilityColors.border.default};
  border-radius: 6px;
`

const StyleColorThumb = styled(ColorThumb)`
  border: 2px solid white;
  box-shadow: 0 0 0 1px black, inset 0 0 0 1px black;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  box-sizing: border-box;

  &[data-focus-visible] {
    width: 24px;
    height: 24px;
  }
`

const StyledColorSlider = styled(ColorSlider)`
  display: grid;
  grid-template-areas:
    'label output'
    'track track';
  grid-template-columns: 1fr auto;
  gap: 4px;
  max-width: 300px;

  .react-aria-Label {
    grid-area: label;
  }

  .react-aria-SliderOutput {
    grid-area: output;
  }

  .react-aria-SliderTrack {
    grid-area: track;
    border-radius: 4px;
  }

  &[data-orientation='horizontal'] {
    .react-aria-SliderTrack {
      height: 28px;
    }

    .react-aria-ColorThumb {
      top: 50%;
    }
  }
`

const StyledColorField = styled(ColorField)`
  display: flex;
  flex-direction: column;
  color: var(--text-color);
`

const StyledInput = styled(Input)`
  padding: 0.286rem;
  margin: 0;
  border: 1px solid ${accessibilityColors.border.default};
  border-radius: 6px;
  background: var(--field-background);
  font-size: 1.143rem;
  color: var(--field-text-color);
  width: 100%;
  max-width: 12ch;
  box-sizing: border-box;

  &[data-focused] {
    outline: 2px solid ${(props) => props.color};
    outline-offset: -1px;
  }
`
